import { UseFormReturn } from "react-hook-form";
import { usePlans } from "@/hooks/usePlans";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2 } from "lucide-react";

interface ReviewStepProps {
  form: UseFormReturn<any>;
}

export function ReviewStep({ form }: ReviewStepProps) {
  const { plans } = usePlans();
  const values = form.getValues();
  const selectedPlan = plans?.find(p => p.id === values.planId);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <CheckCircle2 className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">Revisar Configuração</h3>
      </div>
      
      <Accordion type="multiple" defaultValue={["company", "user", "plan", "config"]} className="w-full">
        <AccordionItem value="company">
          <AccordionTrigger>Dados da Empresa</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Nome:</span>
                <span className="font-medium">{values.companyName}</span>
              </div>
              {values.companyCnpj && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">CNPJ:</span>
                  <span className="font-medium">{values.companyCnpj}</span>
                </div>
              )}
              {values.companyEmail && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">E-mail:</span>
                  <span className="font-medium">{values.companyEmail}</span>
                </div>
              )}
              {values.companyPhone && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Telefone:</span>
                  <span className="font-medium">{values.companyPhone}</span>
                </div>
              )}
              {values.companyDomain && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Domínio:</span>
                  <span className="font-medium">{values.companyDomain}</span>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="user">
          <AccordionTrigger>Dados do Usuário</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Nome:</span>
                <span className="font-medium">{values.userName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">E-mail:</span>
                <span className="font-medium">{values.userEmail}</span>
              </div>
              {values.userPhone && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Telefone:</span>
                  <span className="font-medium">{values.userPhone}</span>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="plan">
          <AccordionTrigger>Plano Selecionado</AccordionTrigger>
          <AccordionContent>
            {selectedPlan && (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-base">{selectedPlan.name}</span>
                  <Badge variant="default">R$ {selectedPlan.price_monthly}/mês</Badge>
                </div>
                
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Usuários:</span>
                    <span className="font-medium">
                      {selectedPlan.max_users === 0 ? 'Ilimitado' : selectedPlan.max_users}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Integrações:</span>
                    <span className="font-medium">{selectedPlan.max_integrations}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Metas:</span>
                    {selectedPlan.goals_enabled ? (
                      <Badge variant="default" className="text-xs">Habilitado</Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">Desabilitado</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Import. Massa:</span>
                    {selectedPlan.bulk_import_enabled ? (
                      <Badge variant="default" className="text-xs">Habilitado</Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">Desabilitado</Badge>
                    )}
                  </div>
                </div>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="config">
          <AccordionTrigger>Configurações do Plano</AccordionTrigger>
          <AccordionContent>
            {selectedPlan && (
              <div className="space-y-4">
                <div>
                  <h5 className="text-sm font-semibold mb-2">Métricas Básicas</h5>
                  <div className="flex flex-wrap gap-2">
                    {selectedPlan.basic_metrics.map((metric) => (
                      <Badge key={metric} variant="outline" className="text-xs">
                        {metric}
                      </Badge>
                    ))}
                  </div>
                </div>

                {selectedPlan.advanced_metrics_enabled && (
                  <div>
                    <h5 className="text-sm font-semibold mb-2">
                      Métricas Avançadas
                      {selectedPlan.advanced_metrics_mode === 'customizable' &&
                        values.customAdvancedMetrics?.length > 0 && (
                          <span className="text-muted-foreground font-normal ml-1">
                            ({values.customAdvancedMetrics.length} selecionadas)
                          </span>
                        )}
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {selectedPlan.advanced_metrics_mode === 'predefined' ? (
                        selectedPlan.advanced_metrics.map((metric) => (
                          <Badge key={metric} variant="secondary" className="text-xs">
                            {metric}
                          </Badge>
                        ))
                      ) : (
                        values.customAdvancedMetrics?.map((metric: string) => (
                          <Badge key={metric} variant="default" className="text-xs">
                            {metric}
                          </Badge>
                        ))
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <h5 className="text-sm font-semibold mb-2">
                    Integrações Permitidas
                    {selectedPlan.integrations_mode === 'customizable' &&
                      values.customIntegrations?.length > 0 && (
                        <span className="text-muted-foreground font-normal ml-1">
                          ({values.customIntegrations.length} selecionadas)
                        </span>
                      )}
                  </h5>
                  <div className="flex flex-wrap gap-2">
                    {selectedPlan.integrations_mode === 'predefined' ? (
                      selectedPlan.allowed_integrations.map((integration) => (
                        <Badge key={integration} variant="secondary" className="text-xs">
                          {integration}
                        </Badge>
                      ))
                    ) : (
                      values.customIntegrations?.map((integration: string) => (
                        <Badge key={integration} variant="default" className="text-xs">
                          {integration}
                        </Badge>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
